import { useState } from 'react'
import Navbar from '../components/Navbar'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const SECTIONS = [
  {
    id:    'claim',
    icon:  '🏷',
    title: 'Claim a Listed Business',
    sub:   'Own or manage a business that\'s already listed? Claim it to edit your info and manage your presence.',
    color: '#b45309',
    bg:    '#fffbeb',
    border:'#fde68a',
  },
  {
    id:    'removal',
    icon:  '🗑',
    title: 'Request Listing Removal',
    sub:   'Want a listing taken down? If you\'re the owner or representative, submit a removal request and we\'ll verify and remove it.',
    color: '#dc2626',
    bg:    '#fef2f2',
    border:'#fecaca',
  },
  {
    id:    'general',
    icon:  '💬',
    title: 'General Support',
    sub:   'Questions, feedback, or anything else? Reach out and we\'ll get back to you.',
    color: '#1a56db',
    bg:    '#eff6ff',
    border:'#bfdbfe',
  },
]

export default function SupportPage() {
  const { user } = useAuth()
  const [active, setActive]   = useState(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone]       = useState(null)
  const [error, setError]     = useState('')

  const [claimForm, setClaimForm] = useState({
    listing_name: '', claimant_name: user?.user_metadata?.username || '',
    claimant_email: user?.email || '', role_at_biz: 'owner',
    proof_url: '', proof_notes: '',
  })

  const [removalForm, setRemovalForm] = useState({
    listing_name: '', listing_url: '',
    your_name: '', your_email: user?.email || '',
    your_role: 'owner', reason: '', details: '',
  })

  const [generalForm, setGeneralForm] = useState({
    name: user?.user_metadata?.username || '',
    email: user?.email || '',
    subject: '', message: '',
  })

  const setC = (k, v) => setClaimForm(f => ({ ...f, [k]: v }))
  const setR = (k, v) => setRemovalForm(f => ({ ...f, [k]: v }))
  const setG = (k, v) => setGeneralForm(f => ({ ...f, [k]: v }))

  async function submitClaim(e) {
    e.preventDefault()
    setError('')
    if (!claimForm.listing_name.trim() || !claimForm.claimant_email.trim()) {
      setError('Please fill in the listing name and your email.'); return
    }
    if (!claimForm.proof_url.trim() && !claimForm.proof_notes.trim()) {
      setError('Please provide at least one form of proof.'); return
    }
    setLoading(true)
    const { error: dbErr } = await supabase.from('ownership_claims').insert({
      listing_name:   claimForm.listing_name,
      claimant_id:    user?.id || null,
      claimant_name:  claimForm.claimant_name,
      claimant_email: claimForm.claimant_email,
      role_at_biz:    claimForm.role_at_biz,
      proof_url:      claimForm.proof_url,
      proof_notes:    claimForm.proof_notes,
    })
    if (dbErr) { setError(dbErr.message); setLoading(false); return }
    try {
      await supabase.functions.invoke('notify-submission', {
        body: { type: 'ownership_claim', name: claimForm.listing_name, flagged: false,
          extra: `Claimed by: ${claimForm.claimant_name} (${claimForm.claimant_email}) as ${claimForm.role_at_biz}` }
      })
    } catch (_) {}
    setLoading(false)
    setDone('claim')
  }

  async function submitRemoval(e) {
    e.preventDefault()
    setError('')
    if (!removalForm.listing_name.trim() || !removalForm.your_email.trim() || !removalForm.reason) {
      setError('Please fill in the listing name, your email, and a reason.'); return
    }
    setLoading(true)
    const { error: dbErr } = await supabase.from('removal_requests').insert({
      ...removalForm, submitted_by: user?.id || null, status: 'open',
    })
    if (dbErr) { setError(dbErr.message); setLoading(false); return }
    try {
      await supabase.functions.invoke('notify-report', {
        body: { targetName: removalForm.listing_name, targetType: 'listing',
          reason: `REMOVAL REQUEST: ${removalForm.reason}`, details: removalForm.details }
      })
    } catch (_) {}
    setLoading(false)
    setDone('removal')
  }

  async function submitGeneral(e) {
    e.preventDefault()
    setError('')
    if (!generalForm.email.trim() || !generalForm.message.trim()) {
      setError('Please fill in your email and message.'); return
    }
    setLoading(true)
    // Log as a general support report entry
    await supabase.from('reports').insert({
      target_id:   '00000000-0000-0000-0000-000000000000',
      target_type: 'support',
      target_name: `General Support: ${generalForm.subject || 'No subject'}`,
      reason:      generalForm.message,
      details:     `From: ${generalForm.name} <${generalForm.email}>`,
      reported_by: user?.id || null,
      status:      'open',
    })
    try {
      await supabase.functions.invoke('notify-report', {
        body: { targetName: `Support: ${generalForm.subject || 'General'}`, targetType: 'support',
          reason: generalForm.message, details: `From: ${generalForm.name} <${generalForm.email}>` }
      })
    } catch (_) {}
    setLoading(false)
    setDone('general')
  }

  const DoneCard = ({ type }) => {
    const msgs = {
      claim:   { icon:'🏷', title:'Claim submitted!', body:`We'll review your ownership claim and follow up at ${claimForm.claimant_email} within a few days. Once verified, you'll be able to edit and manage your listing directly.` },
      removal: { icon:'📬', title:'Removal request received!', body:`We'll review your request for "${removalForm.listing_name}" and follow up at ${removalForm.your_email} within a few days.` },
      general: { icon:'💬', title:'Message received!', body:`Thanks for reaching out. We'll get back to you at ${generalForm.email} as soon as we can.` },
    }
    const m = msgs[type]
    return (
      <div style={{ textAlign:'center', padding:'40px 20px' }}>
        <div style={{ fontSize:52, marginBottom:16 }}>{m.icon}</div>
        <h3 style={{ fontSize:20, fontWeight:800, marginBottom:10 }}>{m.title}</h3>
        <p style={{ fontSize:14, color:'var(--gray-500)', lineHeight:1.65, marginBottom:24, maxWidth:380, margin:'0 auto 24px' }}>{m.body}</p>
        <button onClick={() => { setDone(null); setActive(null); setError('') }} className="btn btn-primary">
          Back to Support
        </button>
      </div>
    )
  }

  return (
    <div>
      <Navbar />
      <div className="page-wrap" style={{ maxWidth: 640 }}>

        <div style={{ marginBottom: 32 }}>
          <div className="page-title">Support</div>
          <div className="page-sub">
            Get help with your listing, claim ownership of a business, or reach out to our team.
          </div>
        </div>

        {done ? <DoneCard type={done} /> : (
          <>
            {/* Section cards */}
            {!active && (
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                {SECTIONS.map(s => (
                  <button key={s.id} onClick={() => { setActive(s.id); setError('') }}
                    style={{
                      background: s.bg, border:`1.5px solid ${s.border}`,
                      borderRadius:14, padding:'20px 22px', textAlign:'left',
                      cursor:'pointer', transition:'all .2s', width:'100%',
                      display:'flex', alignItems:'center', gap:18,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.08)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none' }}
                  >
                    <div style={{ fontSize:32, flexShrink:0 }}>{s.icon}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:16, color: s.color, marginBottom:4 }}>{s.title}</div>
                      <div style={{ fontSize:13, color:'var(--gray-500)', lineHeight:1.5 }}>{s.sub}</div>
                    </div>
                    <div style={{ fontSize:18, color:'var(--gray-300)', flexShrink:0 }}>›</div>
                  </button>
                ))}

                {/* Direct email note */}
                <div style={{ marginTop:8, padding:'14px 18px', background:'var(--gray-50)', border:'1px solid var(--gray-200)', borderRadius:10 }}>
                  <div style={{ fontSize:12, color:'var(--gray-500)', lineHeight:1.6 }}>
                    Prefer email? Reach us directly at{' '}
                    <a href="mailto:crew@windsportsdirectory.com" style={{ color:'var(--blue)', fontWeight:600 }}>
                      crew@windsportsdirectory.com
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Back button when a section is active */}
            {active && (
              <button onClick={() => { setActive(null); setError('') }}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:13, fontWeight:600, fontFamily:'var(--font)', display:'flex', alignItems:'center', gap:5, marginBottom:20, padding:0 }}>
                ← Back
              </button>
            )}

            {error && (
              <div style={{ background:'#fee2e2', color:'#dc2626', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>
                {error}
              </div>
            )}

            {/* ── CLAIM FORM ── */}
            {active === 'claim' && (
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                  <div style={{ fontSize:28 }}>🏷</div>
                  <div>
                    <h2 style={{ fontSize:20, fontWeight:800, marginBottom:2 }}>Claim a Listed Business</h2>
                    <p style={{ fontSize:13, color:'var(--gray-500)' }}>We verify all claims before granting access. False claims may result in account suspension.</p>
                  </div>
                </div>
                <form onSubmit={submitClaim}>
                  <div className="field">
                    <label>Business / listing name *</label>
                    <input value={claimForm.listing_name} onChange={e => setC('listing_name', e.target.value)} placeholder="Exact name as it appears on the directory" required />
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <div className="field">
                      <label>Your name *</label>
                      <input value={claimForm.claimant_name} onChange={e => setC('claimant_name', e.target.value)} placeholder="Full name" required />
                    </div>
                    <div className="field">
                      <label>Your email *</label>
                      <input type="email" value={claimForm.claimant_email} onChange={e => setC('claimant_email', e.target.value)} placeholder="you@yourbusiness.com" required />
                    </div>
                  </div>
                  <div className="field">
                    <label>Your role at this business *</label>
                    <select value={claimForm.role_at_biz} onChange={e => setC('role_at_biz', e.target.value)}>
                      <option value="owner">Owner</option>
                      <option value="manager">Manager</option>
                      <option value="employee">Employee</option>
                      <option value="representative">Authorized Representative</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Proof — website or social link <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(recommended)</span></label>
                    <input value={claimForm.proof_url} onChange={e => setC('proof_url', e.target.value)} placeholder="https://yourbusiness.com or facebook.com/yourbusiness" />
                    <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:4 }}>Must clearly show your name or contact info matching the listing.</div>
                  </div>
                  <div className="field">
                    <label>Additional notes <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(optional)</span></label>
                    <textarea value={claimForm.proof_notes} onChange={e => setC('proof_notes', e.target.value)} placeholder="Any other context that helps us verify your claim…" style={{ minHeight:80 }} />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width:'100%' }} disabled={loading}>
                    {loading ? 'Submitting…' : '🏷 Submit Ownership Claim'}
                  </button>
                  <p style={{ fontSize:11, color:'var(--gray-400)', textAlign:'center', marginTop:10 }}>
                    Claims are reviewed manually. We'll email you once verified — usually within a few days.
                  </p>
                </form>
              </div>
            )}

            {/* ── REMOVAL FORM ── */}
            {active === 'removal' && (
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                  <div style={{ fontSize:28 }}>🗑</div>
                  <div>
                    <h2 style={{ fontSize:20, fontWeight:800, marginBottom:2 }}>Request Listing Removal</h2>
                    <p style={{ fontSize:13, color:'var(--gray-500)' }}>We verify removal requests before taking action. Process typically takes 2-3 business days.</p>
                  </div>
                </div>
                <form onSubmit={submitRemoval}>
                  <div className="field">
                    <label>Listing name *</label>
                    <input value={removalForm.listing_name} onChange={e => setR('listing_name', e.target.value)} placeholder="Exact name as it appears on the directory" required />
                  </div>
                  <div className="field">
                    <label>Listing URL or location <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(optional)</span></label>
                    <input value={removalForm.listing_url} onChange={e => setR('listing_url', e.target.value)} placeholder="Link to the listing or city/region it's under" />
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <div className="field">
                      <label>Your name *</label>
                      <input value={removalForm.your_name} onChange={e => setR('your_name', e.target.value)} placeholder="Full name" required />
                    </div>
                    <div className="field">
                      <label>Your email *</label>
                      <input type="email" value={removalForm.your_email} onChange={e => setR('your_email', e.target.value)} placeholder="you@example.com" required />
                    </div>
                  </div>
                  <div className="field">
                    <label>Your relationship to the listing *</label>
                    <select value={removalForm.your_role} onChange={e => setR('your_role', e.target.value)}>
                      <option value="owner">I am the owner / operator</option>
                      <option value="representative">I am an authorized representative</option>
                      <option value="employee">I am an employee</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Reason for removal *</label>
                    <select value={removalForm.reason} onChange={e => setR('reason', e.target.value)} required>
                      <option value="">Select a reason…</option>
                      <option value="Business is permanently closed">Business is permanently closed</option>
                      <option value="Incorrect or outdated information">Incorrect or outdated information</option>
                      <option value="We did not authorize this listing">We did not authorize this listing</option>
                      <option value="Privacy concern">Privacy concern</option>
                      <option value="Relocating or rebranding">Relocating or rebranding</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Additional details <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(optional)</span></label>
                    <textarea value={removalForm.details} onChange={e => setR('details', e.target.value)} placeholder="Anything else we should know…" style={{ minHeight:80 }} />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width:'100%' }} disabled={loading}>
                    {loading ? 'Submitting…' : '📬 Submit Removal Request'}
                  </button>
                </form>
              </div>
            )}

            {/* ── GENERAL SUPPORT FORM ── */}
            {active === 'general' && (
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                  <div style={{ fontSize:28 }}>💬</div>
                  <div>
                    <h2 style={{ fontSize:20, fontWeight:800, marginBottom:2 }}>General Support</h2>
                    <p style={{ fontSize:13, color:'var(--gray-500)' }}>Questions, feedback, bug reports, or anything else.</p>
                  </div>
                </div>
                <form onSubmit={submitGeneral}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <div className="field">
                      <label>Your name</label>
                      <input value={generalForm.name} onChange={e => setG('name', e.target.value)} placeholder="Optional" />
                    </div>
                    <div className="field">
                      <label>Your email *</label>
                      <input type="email" value={generalForm.email} onChange={e => setG('email', e.target.value)} placeholder="you@example.com" required />
                    </div>
                  </div>
                  <div className="field">
                    <label>Subject <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(optional)</span></label>
                    <input value={generalForm.subject} onChange={e => setG('subject', e.target.value)} placeholder="e.g. Question about my listing, Bug report…" />
                  </div>
                  <div className="field">
                    <label>Message *</label>
                    <textarea value={generalForm.message} onChange={e => setG('message', e.target.value)} placeholder="What can we help you with?" style={{ minHeight:120 }} required />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width:'100%' }} disabled={loading}>
                    {loading ? 'Sending…' : '💬 Send Message'}
                  </button>
                  <p style={{ fontSize:11, color:'var(--gray-400)', textAlign:'center', marginTop:10 }}>
                    Or email us directly at{' '}
                    <a href="mailto:crew@windsportsdirectory.com" style={{ color:'var(--blue)' }}>crew@windsportsdirectory.com</a>
                  </p>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
